import { connect } from 'react-redux'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CountriesSelectors from 'Stores/Country/Selectors'
import CountriesActions from 'Stores/Country/Actions'
import AuthSelectors from 'Stores/Auth/Selectors'
import { useTranslation } from 'react-i18next'
import {
  IFButton,
  IFsvg,
  IFTable,
  IFText,
  IFTooltipIconsLoading,
} from 'Components'
import {
  Box,
  IconButton,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
} from '@mui/material'
import ReportIcon from '@mui/icons-material/Report'
import RequestState from 'Enums/RequestState'
import Styles from './CountriesList.module.css'
import { Colors } from 'Theme'
import ReactCountryFlag from 'react-country-flag'
import styled from 'styled-components'
import InfinityEnums from 'Enums/InfinityEnums'
import { useLocation } from 'react-router-dom'
const Countries = require('world-countries/countries.json')

const IconDivIFToolTip = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 5px;
  transition: background-color 0.3s ease;
  cursor: pointer;
  &:hover {
    background-color: ${Colors.onHoverBackground};
  }
`
const CountriesList = ({
  countries,
  paginationOffset,
  countriesFilters,
  tablesHeight,
  fetchCountriesListRequestState,
  fetchCountriesList,
}) => {
  const { t } = useTranslation()
  const [isFirstLoaded, setIsFirstLoaded] = useState(true)
  const [failedRequest, setFailedRequest] = useState(false)
  const location = useLocation()
  useEffect(() => {
    if (location.state?.refresh) {
      setIsFirstLoaded(true)
    }
  }, [location.state?.refresh])

  useEffect(() => {
    if (
      fetchCountriesListRequestState === RequestState.ERROR_0_NETWORK ||
      fetchCountriesListRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchCountriesListRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchCountriesListRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchCountriesListRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchCountriesListRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchCountriesListRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
      setIsFirstLoaded(false)
    }
  }, [fetchCountriesListRequestState])
  const loadMoreData = () => {
    if (
      fetchCountriesListRequestState === RequestState.LOADING ||
      paginationOffset === null
    )
      return
    fetchCountriesList(countriesFilters, paginationOffset, failedRequest)
  }

  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  const countriesTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header:
          fetchCountriesListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('CountriesPage.CountryName')
          ),
        Cell: ({ row }) => {
          return row.index < countries.length ? (
            <div className={Styles.CountryItem}>
              <ReactCountryFlag
                className={Styles.FlagItem}
                countryCode={row.original.codeTwoLetters}
                svg
              />
              <IFText>{row.original.name}</IFText>
            </div>
          ) : failedRequest ? (
            <div className={Styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'code',
        header:
          fetchCountriesListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('CountriesPage.CountryCode')
          ),
        Cell: ({ row }) => {
          return row.index < countries.length ? (
            <IFText>{row.original.codeTwoLetters}</IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'currency',
        header:
          fetchCountriesListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('CountriesPage.Currency')
          ),
        Cell: ({ row }) => {
          return row.index < countries.length ? (
            <IFText>{row.original.currency.name}</IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],
    [countries, fetchCountriesListRequestState, isFirstLoaded, failedRequest],
  )

  return (
    <>
      <IFTable
        columns={countriesTableColumns}
        data={countries}
        fetchMoreOnBottomReached={fetchMoreOnBottomReached}
        paginationOffset={paginationOffset}
        filters={countriesFilters}
        showSkeleton={
          (fetchCountriesListRequestState === RequestState.LOADING &&
            paginationOffset === 0) ||
          (fetchCountriesListRequestState !== RequestState.SUCCEEDED &&
            !paginationOffset)
        }
        tableMaxHeight={tablesHeight - 16}
        tableSetHeight={'100%'}
        tableRequestState={fetchCountriesListRequestState}
      />
    </>
  )
}
function mapDispatchToProps(dispatch) {
  return {
    fetchCountriesList: (filter, offset, shouldShowError) =>
      dispatch(
        CountriesActions.fetchCountriesList(filter, offset, shouldShowError),
      ),
  }
}
const mapStateToProps = (state) => ({
  countries: CountriesSelectors.getCountriesList(state),
  countriesFilters: CountriesSelectors.getCountriesFilters(state),
  fetchCountriesListRequestState:
    CountriesSelectors.getFetchCountriesListRequestState(state),
  paginationOffset: CountriesSelectors.getCountriesListOffset(state),
})
export default connect(mapStateToProps, mapDispatchToProps)(CountriesList)
